import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
// import AccountantLayout from './AccountantLayout';
import { FaSearch, FaUser, FaBuilding, FaCheckCircle, FaClipboardList, FaFileInvoiceDollar, FaUsers, FaEye, FaTimes, FaPhone, FaCalendarAlt } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';

const AccountantWorkAllocation = () => {
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [selectedAllocation, setSelectedAllocation] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {};
        let socData = {};
        let loadedNodes = 0;

        const processData = () => {
            if (loadedNodes < 2) return;

            const allAllocations = [];

            // Process Engineers
            Object.keys(engData).forEach(key => {
                const user = engData[key];
                if (user.work_payment) {
                    Object.keys(user.work_payment).forEach(meetingNum => {
                        const payment = user.work_payment[meetingNum];
                        allAllocations.push({
                            id: `${key}-${meetingNum}`,
                            userKey: key,
                            userNode: 'ekamwatapzp/EngineerRegistrations',
                            userName: user.firstName + ' ' + (user.surname || ''),
                            mobile: user.whatsappNo,
                            type: 'Individual Engineer',
                            meetingNumber: meetingNum,
                            selectedWorks: payment.selectedWorks || [],
                            receiptId: payment.receiptId,
                            date: payment.date,
                            status: payment.status
                        });
                    });
                }
            });

            // Process Societies
            Object.keys(socData).forEach(key => {
                const user = socData[key];
                if (user.work_payment) {
                    Object.keys(user.work_payment).forEach(meetingNum => {
                        const payment = user.work_payment[meetingNum];
                        allAllocations.push({
                            id: `${key}-${meetingNum}`,
                            userKey: key,
                            userNode: 'ekamwatapzp/SocietyRegistrations',
                            userName: user.societyName,
                            mobile: user.chairmanWhatsapp,
                            type: 'Society',
                            meetingNumber: meetingNum,
                            selectedWorks: payment.selectedWorks || [],
                            receiptId: payment.receiptId,
                            date: payment.date,
                            status: payment.status
                        });
                    });
                }
            });

            // Sort by date (newest first)
            setAllocations(allAllocations.sort((a, b) => new Date(b.date) - new Date(a.date)));
            setLoading(false);
        };

        const unsubEng = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            loadedNodes++;
            processData();
        });

        const unsubSoc = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            loadedNodes++;
            processData();
        });

        return () => {
            unsubscribeAuth();
            unsubEng();
            unsubSoc();
        };
    }, [navigate]);

    const handleViewDetails = (item) => {
        setSelectedAllocation(item);
        setShowModal(true);
    };

    const handleStatusUpdate = async (status) => {
        if (!selectedAllocation) return;

        try {
            const statusValue = status === 'accept' ? 'accepted by accountant' : 'rejected by accountant';
            const paymentRef = ref(db, `${selectedAllocation.userNode}/${selectedAllocation.userKey}/work_payment/${selectedAllocation.meetingNumber}`);
            
            await update(paymentRef, {
                status: statusValue,
                updatedAt: new Date().toISOString()
            });

            // Update local state temporarily for immediate UI feedback
            setAllocations(prev => prev.map(item => 
                item.id === selectedAllocation.id ? { ...item, status: statusValue } : item
            ));
            setSelectedAllocation(prev => ({ ...prev, status: statusValue }));
            
            alert(`पेमेंट यशस्वीरित्या ${status === 'accept' ? 'स्वीकारले' : 'नाकारले'}!`);
        } catch (error) {
            console.error("Error updating status:", error);
            alert("स्थिती अपडेट करताना त्रुटी आली: " + error.message);
        }
    };

    const filteredData = allocations.filter(item => {
        const matchesSearch = item.userName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             item.mobile.includes(searchTerm) ||
                             item.receiptId?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || 
                           (filterType === 'engineer' && item.type === 'Individual Engineer') ||
                           (filterType === 'society' && item.type === 'Society');
        return matchesSearch && matchesType;
    });

    const stats = {
        total: allocations.length,
        engineers: allocations.filter(a => a.type === 'Individual Engineer').length,
        societies: allocations.filter(a => a.type === 'Society').length
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="animate-spin h-12 w-12 text-blue-600" />
            </div>
        );
    }

    return (
        <div className="p-8 bg-slate-50 min-h-screen">
                <div className="mb-8 ">
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">काम पेमेंट तपशील (Work Payment Details)</h1>
                    <p className="text-gray-500 font-bold mt-1 uppercase text-xs tracking-widest">ज्या वापरकर्त्यांनी पेमेंट पूर्ण केले आहे त्यांची यादी</p>
                </div>

                {/* Stats Header */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex items-center gap-5">
                        <div className="bg-blue-100 p-4 rounded-2xl text-blue-600">
                            <FaFileInvoiceDollar size={24} />
                        </div>
                        <div>
                            <div className="text-2xl font-black text-gray-900">{stats.total}</div>
                            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">एकूण पेमेंट (Total Payments)</div>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex items-center gap-5">
                        <div className="bg-green-100 p-4 rounded-2xl text-green-600">
                            <FaUser size={24} />
                        </div>
                        <div>
                            <div className="text-2xl font-black text-gray-900">{stats.engineers}</div>
                            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">अभियंता पेमेंट (Engineers)</div>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex items-center gap-5">
                        <div className="bg-amber-100 p-4 rounded-2xl text-amber-600">
                            <FaBuilding size={24} />
                        </div>
                        <div>
                            <div className="text-2xl font-black text-gray-900">{stats.societies}</div>
                            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">संस्था पेमेंट (Societies)</div>
                        </div>
                    </div>
                </div>

                {/* Search & Filter */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mb-8 flex flex-col md:flex-row gap-6">
                    <div className="flex-grow relative">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="शोध (नाव, मोबाईल किंवा पावती क्रमांक)..." 
                            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-100 focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select 
                        className="bg-gray-50 border-none rounded-2xl px-6 py-3 outline-none focus:ring-4 focus:ring-blue-50 font-bold text-gray-700 cursor-pointer"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="all">सर्व प्रकार (All Types)</option>
                        <option value="engineer">अभियंता (Engineer)</option>
                        <option value="society">संस्था (Society)</option>
                    </select>
                </div>

                <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-900 text-white">
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em]">वापरकर्ता (User Name)</th>
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-center">बैठक क्र.</th>
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em]">निवडलेली कामे (Selected Works)</th>
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em]">पेमेंट तपशील (Payment)</th>
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-center">स्थिती (Status)</th>
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-center">कृती (Action)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredData.map((item) => (
                                    <tr key={item.id} className="hover:bg-blue-50/30 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center space-x-4">
                                                <div className={`p-3 rounded-2xl ${item.type === 'Society' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                                                    {item.type === 'Society' ? <FaBuilding size={20} /> : <FaUser size={20} />}
                                                </div>
                                                <div>
                                                    <div className="font-black text-gray-900 text-lg group-hover:text-blue-600 transition-colors tracking-tight">{item.userName}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-black uppercase tracking-widest">{item.type}</span>
                                                        <span className="text-xs font-bold text-gray-400">{item.mobile}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <div className="inline-block bg-indigo-50 text-indigo-700 px-4 py-2 rounded-xl font-black text-lg border-2 border-indigo-100">
                                                {item.meetingNumber}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex flex-wrap gap-2">
                                                {item.selectedWorks.map((work, idx) => (
                                                    <span key={idx} className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-xl font-bold text-xs border border-emerald-100 flex items-center gap-2">
                                                        <FaClipboardList size={12} className="opacity-50" />
                                                        {work}
                                                    </span>
                                                ))}
                                                {item.selectedWorks.length === 0 && (
                                                    <span className="text-gray-400 italic text-sm font-medium">No works selected</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="space-y-1">
                                                <div className="text-sm font-black text-gray-800 font-mono">{item.receiptId}</div>
                                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                    {item.date ? new Date(item.date).toLocaleDateString('en-GB') : '-'} {item.date ? new Date(item.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <div className={`flex items-center justify-center gap-2 font-black text-xs px-4 py-2 rounded-full border uppercase tracking-widest shadow-sm ${
                                                item.status === 'accepted by accountant' ? 'text-green-600 bg-green-50 border-green-100' :
                                                item.status === 'rejected by accountant' ? 'text-red-600 bg-red-50 border-red-100' :
                                                'text-blue-600 bg-blue-50 border-blue-100'
                                            }`}>
                                                {item.status === 'accepted by accountant' && <FaCheckCircle className="animate-pulse" />}
                                                {item.status === 'rejected by accountant' ? 'REJECTED' : 
                                                 item.status === 'accepted by accountant' ? 'ACCEPTED' : 
                                                 'PAID / PENDING'}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <button 
                                                onClick={() => handleViewDetails(item)}
                                                className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-2xl transition-all shadow-lg shadow-blue-100 active:scale-95 group/btn"
                                                title="तपशील पहा (View Details)"
                                            >
                                                <FaEye className="group-hover/btn:scale-110 transition-transform" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredData.length === 0 && (
                            <div className="p-20 text-center flex flex-col items-center justify-center">
                                <div className="bg-gray-100 p-6 rounded-full text-gray-300 mb-4">
                                    <FaUsers size={48} />
                                </div>
                                <p className="text-gray-400 font-black uppercase tracking-[0.2em]">कोणतीही पेमेंट नोंद आढळली नाही (No Payment Records)</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Detail Modal */}
                {showModal && selectedAllocation && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                        <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300 border border-white">
                            <div className="bg-slate-900 p-8 text-white flex justify-between items-center relative">
                                <div className="flex items-center gap-4">
                                    <div className={`p-4 rounded-[1.5rem] ${selectedAllocation.type === 'Society' ? 'bg-amber-400/20 text-amber-400' : 'bg-blue-400/20 text-blue-400'}`}>
                                        {selectedAllocation.type === 'Society' ? <FaBuilding size={24} /> : <FaUser size={24} />}
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black tracking-tight">{selectedAllocation.userName}</h3>
                                        <p className="text-slate-400 font-bold text-xs uppercase tracking-widest mt-1">{selectedAllocation.type} Profile</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setShowModal(false)}
                                    className="bg-white/10 hover:bg-white/20 p-3 rounded-2xl transition-all"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <div className="p-8 space-y-8">
                                {/* Details Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100">
                                        <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <FaPhone className="text-blue-500" /> संपर्क (Contact)
                                        </div>
                                        <div className="text-lg font-black text-slate-900">{selectedAllocation.mobile}</div>
                                    </div>
                                    <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100">
                                        <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <FaCalendarAlt className="text-indigo-500" /> बैठक क्र. (Meeting)
                                        </div>
                                        <div className="text-lg font-black text-slate-900">बैठक क्रमांक {selectedAllocation.meetingNumber}</div>
                                    </div>
                                </div>

                                {/* Works Section */}
                                <div className="space-y-4">
                                    <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] flex items-center gap-2">
                                        <FaClipboardList className="text-emerald-500" /> निवडलेली कामे (Selected Works)
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {selectedAllocation.selectedWorks.map((work, idx) => (
                                            <div key={idx} className="bg-emerald-50 text-emerald-700 px-5 py-3 rounded-2xl font-black text-sm border-2 border-emerald-100 flex items-center gap-3">
                                                <div className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-[10px]">
                                                    {idx + 1}
                                                </div>
                                                {work}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Payment Receipt Section */}
                                <div className="bg-blue-600 rounded-3xl p-6 text-white shadow-xl shadow-blue-100">
                                    <div className="flex justify-between items-center border-b border-blue-400/30 pb-4 mb-4">
                                        <div className="text-[10px] font-black uppercase tracking-widest opacity-70">पेमेंट पावती (Payment Receipt)</div>
                                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                            selectedAllocation.status === 'accepted by accountant' ? 'bg-green-400 text-white' :
                                            selectedAllocation.status === 'rejected by accountant' ? 'bg-red-400 text-white' :
                                            'bg-white/20 text-white'
                                        }`}>
                                            {selectedAllocation.status === 'accepted by accountant' ? 'ACCEPTED' : 
                                             selectedAllocation.status === 'rejected by accountant' ? 'REJECTED' : 'PAID'}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">Receipt ID</div>
                                            <div className="font-black text-lg font-mono">{selectedAllocation.receiptId}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">Transaction Date</div>
                                            <div className="font-black text-lg">
                                                {selectedAllocation.date ? new Date(selectedAllocation.date).toLocaleDateString('en-GB') : '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-8 pt-0 flex flex-col items-center gap-6">
                                <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">Approval Status</div>
                                
                                <div className="flex bg-slate-100 p-1.5 rounded-[2rem] w-full max-w-sm relative">
                                    <button 
                                        onClick={() => handleStatusUpdate('accept')}
                                        className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[1.5rem] font-black text-sm transition-all z-10 ${
                                            selectedAllocation.status === 'accepted by accountant' 
                                            ? 'bg-green-600 text-white shadow-lg' 
                                            : 'text-slate-400 hover:text-slate-600'
                                        }`}
                                    >
                                        <FaCheckCircle /> ACCEPTED
                                    </button>
                                    <button 
                                        onClick={() => handleStatusUpdate('reject')}
                                        className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[1.5rem] font-black text-sm transition-all z-10 ${
                                            selectedAllocation.status === 'rejected by accountant' 
                                            ? 'bg-red-600 text-white shadow-lg' 
                                            : 'text-slate-400 hover:text-slate-600'
                                        }`}
                                    >
                                        <FaTimes /> REJECTED
                                    </button>
                                </div>

                                <button 
                                    onClick={() => setShowModal(false)}
                                    className="w-full py-4 text-slate-400 hover:text-slate-600 font-black text-[10px] transition-all uppercase tracking-widest"
                                >
                                    बंद करा (Close Window)
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
    );
};

export default AccountantWorkAllocation;
